import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PhoneCall, MessageSquare, Zap, ShieldCheck, Timer, Radio, Waves, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { lrProductInfo } from "@/lib/api";

const VALUE_PROPS = [
  { icon: Timer, title: "Respond in seconds",
    desc: "Every eligible website inquiry triggers an immediate call, SMS or email — before interest fades." },
  { icon: Radio, title: "AI + human, side-by-side",
    desc: "The AI answers first, humans take over the moment a caller needs one — one conversation, one record." },
  { icon: Waves, title: "Cadences that respect people",
    desc: "Follow-ups are consented, quiet-hours-aware, opt-out-honoring — persistent without being pushy." },
  { icon: ShieldCheck, title: "Compliance built in",
    desc: "Consent ledger, jurisdictional calling windows, STOP enforcement, AI disclosure — all first-class." },
];

const HOW_IT_WORKS = [
  { step: "1", title: "Every website form, chat, and missed call feeds one lead ledger",
    detail: "We wire your forms + chat + phone into LCC — one record per person, deduplicated automatically." },
  { step: "2", title: "The orchestrator picks the first eligible channel",
    detail: "It respects consent, quiet hours, calling windows and channel availability — before it does anything else." },
  { step: "3", title: "AI or human, whichever is ready first",
    detail: "AI receptionist answers 24/7; humans get the hot ones during business hours; AI-to-human handoff is seamless." },
  { step: "4", title: "Follow-up cadence runs on its own",
    detail: "SMS acknowledgement in 2 minutes, retry call in 30, personalized email next day — stops the moment the lead answers, books or opts out." },
  { step: "5", title: "You see every second of response time",
    detail: "TTFR (time-to-first-response), TTFC, SLA compliance, appointment + conversion attribution — all in one dashboard." },
];

const INDUSTRIES = ["Dental", "Medical", "Automotive", "Restaurant", "Real estate", "Home services", "Professional services", "Retail"];

export default function LeadResponsePage() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    document.title = "AI Lead Response & Calling | WebsiteTech.pro";
    lrProductInfo().then(setInfo).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1" data-testid="lr-marketing">
        {/* Hero */}
        <section className="relative overflow-hidden pt-28 pb-16 text-white bg-slate-950">
          <div className="absolute -right-32 -top-32 w-[520px] h-[520px] rounded-full bg-amber-400 opacity-20 blur-3xl pointer-events-none" />
          <div className="absolute -left-32 bottom-0 w-[420px] h-[420px] rounded-full bg-indigo-500 opacity-25 blur-3xl pointer-events-none" />
          <div className="relative max-w-6xl mx-auto px-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
              <Zap className="w-3.5 h-3.5" /> AI Lead Response & Calling
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]">
              Respond to every lead <span className="text-amber-400">while interest is still high.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-3xl">
              {info?.tagline || "Turn every website inquiry into an immediate, intelligent conversation — by voice, text, WhatsApp or email."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="lr-hero-demo"
                className="inline-flex items-center gap-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-950 px-6 py-3.5 font-bold transition">
                Book a demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/business-center/calculators/speed-to-lead" data-testid="lr-hero-roi"
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 px-6 py-3.5 font-semibold transition">
                Estimate ROI
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
              {(info?.modules || []).map((m) => (
                <div key={m.key} data-testid={`lr-module-${m.key}`}
                  className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">{m.key.replace("-", " ")}</div>
                  <div className="mt-1 text-sm font-bold">{m.label}</div>
                  <div className="mt-1 text-[11px] text-slate-400 leading-snug">{m.description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Value props */}
        <section className="py-16 bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">Why this exists</div>
            <h2 className="mt-1 text-2xl md:text-3xl font-bold text-slate-900 max-w-3xl">
              Speed to lead is the single biggest lever most businesses never pull.
            </h2>
            <p className="mt-2 text-slate-600 max-w-3xl">Research shows the first 5 minutes after a form submission are worth more than the next 5 hours combined. This product exists to make sure you never lose that window again.</p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {VALUE_PROPS.map((v) => {
                const Icon = v.icon;
                return (
                  <div key={v.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid={`lr-value-${v.title.split(" ")[0].toLowerCase()}`}>
                    <div className="w-11 h-11 rounded-lg bg-indigo-600 text-white grid place-items-center"><Icon className="w-5 h-5" /></div>
                    <div className="mt-3 font-bold text-slate-900">{v.title}</div>
                    <div className="mt-1 text-sm text-slate-600 leading-relaxed">{v.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">How it works</div>
            <h2 className="mt-1 text-2xl md:text-3xl font-bold text-slate-900">A complete lead-response operating system, not just a dialer.</h2>
            <div className="mt-8 space-y-4">
              {HOW_IT_WORKS.map((s) => (
                <div key={s.step} className="flex gap-4 rounded-2xl bg-white border border-slate-200 p-5" data-testid={`lr-step-${s.step}`}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-950 text-amber-400 font-black grid place-items-center">{s.step}</div>
                  <div>
                    <div className="font-bold text-slate-900">{s.title}</div>
                    <div className="mt-1 text-sm text-slate-600 leading-relaxed">{s.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Compliance strip */}
        <section className="py-16 bg-slate-950 text-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-emerald-400" />
              <h2 className="text-2xl md:text-3xl font-bold">Compliance is a feature, not fine print.</h2>
            </div>
            <p className="mt-3 text-slate-300 max-w-3xl">Every outbound action passes through the same consent + jurisdiction + suppression checks. Nothing about this product is designed to evade carrier protections or opt-out rules.</p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {(info?.compliance_pillars || []).map((p, i) => (
                <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-3 text-sm flex items-start gap-2" data-testid={`lr-compliance-${i}`}>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> <span>{p}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">Built for</div>
            <h2 className="mt-1 text-2xl md:text-3xl font-bold text-slate-900">Any business where a fast reply wins.</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {INDUSTRIES.map((i) => (
                <span key={i} className="text-sm px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 font-semibold" data-testid={`lr-industry-${i.toLowerCase().replace(" ", "-")}`}>{i}</span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-indigo-600 to-slate-950 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold">See it running on your own leads.</h2>
            <p className="mt-3 text-slate-200">We'll wire it into one of your live forms and show you the response-time telemetry within 48 hours.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/contact" data-testid="lr-cta-book"
                className="inline-flex items-center gap-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-950 px-6 py-3 font-bold transition">
                Book a live demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/portal/lead-response" data-testid="lr-cta-inbox"
                className="inline-flex items-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 px-6 py-3 font-semibold transition">
                Open lead inbox
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
